import React, { useEffect, useState } from 'react';
import { Tab, Form, Table, Button, Icon, Dropdown, Search } from 'semantic-ui-react';
import Select from 'react-select';
import { IContentExclusion, IRestrictionProps } from '../../../interfaces/creation-form';
import {
    ContentExclusionType,
    CONTENT_EXCLUSION_TYPE_OPTIONS,
    getContentExclusionTypeLabel,
} from '../../../enums/content-exclusion-type';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContentProviders } from '../../../../../../redux/routines';
import { State } from '../../../../../../common/types/state';
import { useContentExclusionsTableSearch } from './hooks/use-content-exclusions-table-search';
import './styles.scss';

export interface ContentExclusionsTabProps {
    restrictions: IRestrictionProps;
    setState: (restrictions: IRestrictionProps) => void;
    sportsList: string[];
    competitionsList: string[];
}

const selectMenuPortalProps = {
    menuPortalTarget: document.body,
    styles: {
        menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 }),
    },
};

const ContentExclusionsTab: React.FC<ContentExclusionsTabProps> = ({
    restrictions,
    setState,
    sportsList,
    competitionsList,
}) => {
    const dispatch = useDispatch();
    const { contentProviders } = useSelector((state: State) => state);

    const [sport, setSport] = useState<{ value: string; label: string } | null>(null);
    const [competition, setCompetition] = useState<{ value: string; label: string } | null>(null);
    const [dataProvider, setDataProvider] = useState<{ value: string; label: string } | null>(null);
    const [type, setType] = useState<ContentExclusionType>(ContentExclusionType.Exclude);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        if (!contentProviders?.length) {
            dispatch(fetchContentProviders());
        }
    }, [contentProviders?.length, dispatch]);

    const contentExclusions = restrictions.contentExclusions || [];

    const {
        searchQuery,
        handleSearchChange,
        tableRows,
        hasExclusions,
        hasNoSearchResults,
    } = useContentExclusionsTableSearch(contentExclusions);

    const toSelectOption = (value: string) => ({ value, label: value });

    const buildExclusionKey = (exclusion: Pick<IContentExclusion, 'sport' | 'competition' | 'dataProvider' | 'type'>) =>
        `${exclusion.sport}|${exclusion.competition}|${exclusion.dataProvider}|${exclusion.type}`;

    const clearForm = () => {
        setSport(null);
        setCompetition(null);
        setDataProvider(null);
        setType(ContentExclusionType.Exclude);
        setEditingIndex(null);
        setFormError(null);
    };

    const startEdit = (index: number) => {
        const exclusion = contentExclusions[index];
        setEditingIndex(index);
        setSport(exclusion.sport ? toSelectOption(exclusion.sport) : null);
        setCompetition(exclusion.competition ? toSelectOption(exclusion.competition) : null);
        setDataProvider(exclusion.dataProvider ? toSelectOption(exclusion.dataProvider) : null);
        setType(exclusion.type);
        setFormError(null);
    };

    const cancelEdit = () => {
        clearForm();
    };

    const submitExclusion = () => {
        const sportValue = sport?.value || '';
        const competitionValue = competition?.value || '';
        const dataProviderValue = dataProvider?.value || '';

        if (!sportValue && !competitionValue) {
            setFormError('Specify at least a sport or a competition.');
            return;
        }

        const exclusionData: IContentExclusion = {
            sport: sportValue,
            competition: competitionValue,
            dataProvider: dataProviderValue,
            type,
        };

        if (editingIndex !== null && contentExclusions[editingIndex].id !== undefined) {
            exclusionData.id = contentExclusions[editingIndex].id;
        }

        const isDuplicate = contentExclusions.some(
            (e, index) => index !== editingIndex && buildExclusionKey(e) === buildExclusionKey(exclusionData)
        );

        if (isDuplicate) {
            setFormError('This exclusion rule already exists.');
            return;
        }

        if (editingIndex !== null) {
            setState({
                ...restrictions,
                contentExclusions: contentExclusions.map((e, index) =>
                    index === editingIndex ? exclusionData : e
                ),
            });
        } else {
            setState({
                ...restrictions,
                contentExclusions: [...contentExclusions, exclusionData],
            });
        }

        clearForm();
    };

    const removeExclusion = (index: number) => {
        if (editingIndex === index) {
            clearForm();
        } else if (editingIndex !== null && editingIndex > index) {
            setEditingIndex(editingIndex - 1);
        }

        setState({
            ...restrictions,
            contentExclusions: contentExclusions.filter((_, i) => i !== index),
        });
    };

    const displayValue = (value: string) => value || '—';

    const isEditing = editingIndex !== null;

    return (
        <Tab.Pane>
            <div className="content-exclusions-tab">
                <Form>
                    <div className="content-exclusions-tab__form">
                        <div className="content-exclusions-tab__inputs-row">
                            <Form.Field className="content-exclusions-tab__field">
                                <label>Sport</label>
                                <Select
                                    {...selectMenuPortalProps}
                                    placeholder="Select sport"
                                    isClearable
                                    value={sport}
                                    onChange={(option) => setSport(option ?? null)}
                                    options={sportsList.map(toSelectOption)}
                                />
                            </Form.Field>
                            <Form.Field className="content-exclusions-tab__field">
                                <label>Competition</label>
                                <Select
                                    {...selectMenuPortalProps}
                                    placeholder="Select competition"
                                    isClearable
                                    value={competition}
                                    onChange={(option) => setCompetition(option ?? null)}
                                    options={competitionsList.map(toSelectOption)}
                                />
                            </Form.Field>
                            <Form.Field className="content-exclusions-tab__field">
                                <label>Content Provider</label>
                                <Select
                                    {...selectMenuPortalProps}
                                    placeholder="Select provider"
                                    isClearable
                                    value={dataProvider}
                                    onChange={(option) => setDataProvider(option ?? null)}
                                    options={(contentProviders || []).map(toSelectOption)}
                                />
                            </Form.Field>
                            <Form.Field className="content-exclusions-tab__field content-exclusions-tab__field--type">
                                <label>Type</label>
                                <Dropdown
                                    selection
                                    className="content-exclusions-tab__type-dropdown"
                                    options={CONTENT_EXCLUSION_TYPE_OPTIONS}
                                    value={type}
                                    onChange={(_, data) => setType(data.value as ContentExclusionType)}
                                />
                            </Form.Field>
                            <div className="content-exclusions-tab__form-actions">
                                {isEditing && (
                                    <Button type="button" onClick={cancelEdit}>
                                        Cancel
                                    </Button>
                                )}
                                <Button type="button" color="green" onClick={submitExclusion}>
                                    {isEditing ? 'Edit' : 'Add'}
                                </Button>
                            </div>
                        </div>

                        <div className="content-exclusions-tab__search-row">
                            <Form.Field className="content-exclusions-tab__field content-exclusions-tab__search-field">
                                <label>Search</label>
                                <Search
                                    className="content-exclusions-tab__search"
                                    placeholder="Search..."
                                    onSearchChange={handleSearchChange}
                                    results={[]}
                                    value={searchQuery}
                                    showNoResults={false}
                                />
                            </Form.Field>
                        </div>
                    </div>
                    {formError && <div className="content-exclusions-tab__error">{formError}</div>}
                </Form>

                <div className="content-exclusions-tab__table-wrapper">
                    <Table celled className="content-exclusions-tab__table">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Sport</Table.HeaderCell>
                                <Table.HeaderCell>Competition</Table.HeaderCell>
                                <Table.HeaderCell>Content Provider</Table.HeaderCell>
                                <Table.HeaderCell>Type</Table.HeaderCell>
                                <Table.HeaderCell collapsing>Actions</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {!hasExclusions ? (
                                <Table.Row>
                                    <Table.Cell colSpan={5} textAlign="center">
                                        No content exclusions configured
                                    </Table.Cell>
                                </Table.Row>
                            ) : hasNoSearchResults ? (
                                <Table.Row>
                                    <Table.Cell colSpan={5} textAlign="center">
                                        No exclusions match your search
                                    </Table.Cell>
                                </Table.Row>
                            ) : (
                                tableRows.map(({ exclusion, originalIndex }) => (
                                    <Table.Row
                                        key={
                                            exclusion.id != null
                                                ? exclusion.id
                                                : `new-${buildExclusionKey(exclusion)}-${originalIndex}`
                                        }
                                        className={
                                            originalIndex === editingIndex
                                                ? 'content-exclusions-tab__row--editing'
                                                : ''
                                        }
                                    >
                                        <Table.Cell>{displayValue(exclusion.sport)}</Table.Cell>
                                        <Table.Cell>{displayValue(exclusion.competition)}</Table.Cell>
                                        <Table.Cell>{displayValue(exclusion.dataProvider)}</Table.Cell>
                                        <Table.Cell>{getContentExclusionTypeLabel(exclusion.type)}</Table.Cell>
                                        <Table.Cell className="content-exclusions-tab__actions-cell">
                                            <div className="content-exclusions-tab__table-actions">
                                                <Button
                                                    type="button"
                                                    primary
                                                    icon
                                                    disabled={isEditing && originalIndex !== editingIndex}
                                                    onClick={() => startEdit(originalIndex)}
                                                >
                                                    <Icon name="edit" />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    secondary
                                                    icon
                                                    disabled={isEditing && originalIndex !== editingIndex}
                                                    onClick={() => removeExclusion(originalIndex)}
                                                >
                                                    <Icon name="delete" />
                                                </Button>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                ))
                            )}
                        </Table.Body>
                    </Table>
                </div>
            </div>
        </Tab.Pane>
    );
};

export default ContentExclusionsTab;
